import {InjectionToken} from '@angular/core'
import {TableHolding} from '../summary/savings-goal-summary/table-holding'
import {BreakdownData} from './breakdown-data'

/**
 * The routes are good to be consistent with.
 */
export const LOGIN_PATH = 'login'
export const ADVICE_PATH = 'advice'
export const RTQ_PATH = 'rtq'
export const CUSTOMER_PATH = 'customer'
export const CUSTOMER_START = 'customer-start'
export const MODEL_PORTFOLIO_PATH = 'modelportfolio'

export const USER_ROLE_NAME = 'ir2User'
export const PORTFOLIO_MANAGER_ROLE = 'ir2PfAdmin'

/**
 * User roles, note irUser is being removed in the future
 */
export const USER_ROLES = [USER_ROLE_NAME, 'irUser']

/**
 * Portfolio manager roles, note irPfAdmin is being removed in the future
 */
export const PORTFOLIO_MANAGER_ROLES = [PORTFOLIO_MANAGER_ROLE, 'irPfAdmin']

// The colors in a breakdown chart or table
export const BREAKDOWN_COLORS = ['#1f55a5', '#ef7622', '#518428', '#f5c400', '#e5e5e5']
export const BREAKDOWN_LABELS = ['Aktier', 'Räntor', 'Kassa', 'Övrigt', 'Ej klassificerad']

export const WINDOW = new InjectionToken<Window>('window')

export type TRegionKey = keyof IRegionalExposureValues

/**
 * Personnel/Employees at the Bank and their details
 */
export interface IAdvisor {

  /**
   * The employees full name
   */
  name: string

  /**
   * The employees ...
   */
  sId: string

  /**
   * The employees office, e.g. branch, office, etc.
   */
  office: string
}

/**
 * Basic data that is used in the PDF report
 */
export interface ICustomerBase {
  given_name: string
  family_name: string
  sub: string
  // This is the personal number for someone who is representing a company
  companySignatoryPersonalNumber: string
  email: string
  mobile: string
}

/**
 * A Bank Customer
 */
export interface ICustomer extends ICustomerBase {
  id: string
  isOrganization: boolean
  createdAt?: string
  advisor: IAdvisor
}

/**
 * Account type names for discrimination of accounts types in
 * selects boxes. Note names are dependent on what the actual account
 * coming from the BE uses as a value for its type.
 */
export const OAccountTypeNames = {
  FUND_ACCOUNT: 'Fondkonto',
  ESCROW_ACCOUNT: 'Depåkonto',
  INVESTMENT_SAVINGS_ACCOUNT: 'Investeringssparkonto',
  INDIVIDUAL_PENSIONS_SAVINGS_ACCOUNT: 'Individuellt Pensionssparande',
  DEPOSIT: 'Inlåning',
  ENDOWMENT_INSURANCE: 'Kapitalförsäkring'

} as const

export type TAccountSource = 'ABASEC' | 'EDW' | 'CUSTOM'
export type THoldingIdType = 'ISIN' | 'MSID'
export type THoldingsChanges = Record<string, number>
export type TPortfolioId = 'org' | 'new'
// The status of an advice signature
export type TSignStatus = 'manual' | 'snfsr' | 'undecided'

// TODO Remove when updating bar charts
// Värdepappers variables
export interface IPaperInfo {
  amount: number
  percent: number
  name: string
}

export interface IAdvice {
  adviceId: string
  name: string
  createdAt: string
  // The status of the signed advice
  signed: TSignStatus
  // The date the advice was signed
  signedDate: string
  // The id of the document that was signed
  documentId: string
  advisor: IAdvisor
  customerId: string
  rtqId: string
  savingsGoals: ISavingsGoal[]
  notes: INotes
  includeAllFundFactSheets: boolean
  version: number
}

// A used account to be displayed when listing used accounts
export interface IUsedAccount {
  account: IAccount
  totalAmount: number
  cash: number
  reInvest: number
  checked: boolean
}

export interface IEditPortfolio {
  holdings: IHolding[]
  modifiedAccounts: Map<string, IModifiedAccount>
}

export interface ISavingsGoalBase {
  id: string
  horizon: IHorizon
  portfolio: IPortfolio | null
}

export interface ISavingsGoal extends ISavingsGoalBase {
  modifiedAccounts: Map<string, IModifiedAccount>
}

export interface ISavingsGoalServer extends ISavingsGoalBase {
  modifiedAccounts: any
}

export interface IAccountModifiedHoldings {
  account: IAccount
  modifiedAccounts: THoldingsChanges
}

export interface ITableData {
  label: string
  value: number,
  percent: number,
  color: string
}

export interface IAccount {
  /**
   * Application specific unique identifier for the account, e.g. 1, 2 etc
   */
  id: string

  /**
   * Type name, e.g. "Fondkonto", "Individuellt Pensionssparande", etc.
   */
  type: string

  /**
   * The account number, e.g. "1234567890" or "ABC123" or "XYZ123" etc.
   */
  accountNumber: string

  /**
   * Name of the account, initially the account number. Can be changed for convenience
   * and better/easier identification
   */
  name: string

  /**
   * Optional note for the account.
   */
  note?: string

  /**
   * Source of the account, either 'abasec' or 'custom'
   */
  source: TAccountSource

  /**
   * Account holdings associated with the account
   */
  holdings: IHolding[]
}

export interface IAccountCheckbox extends IAccount {
  checked: boolean
}

// This is the base data that we send to Morningstar
export interface IMorningstarRequestDataBase {
  identifierType: string
  identifier: string
}

// We use this for values
export interface IMorningstarAmountRequestData extends IMorningstarRequestDataBase {
  amount: number
}

// We use this for weight
export interface IMorningstarWeightRequestData extends IMorningstarRequestDataBase {
  weight: number
}

// Cost summary we get from Morningstar
export interface IMorningstarCosts {
  cost: ICost
}

export interface ICost {
  portfolios: IPortfolioCosts[]
}

export interface IPortfolioCosts {
  holdingFees: IFees[]
  summary: IFeesSummary
  portfolioId: TPortfolioId
}

export interface IFeesSummary {
  returnExcludingFeesAmount: number
  returnIncludingFeesAmount: number
  reductionInReturnAmount: number
  reductionInReturnPercentage: number
  totalCostAmount: number
  serviceCostAmount: number
  serviceCostPercentage: number
  initialProductCostAmount: number
  initialProductCostPercentage: number
  productOngoingCostAmount: number
  productOngoingCostPercentage: number
  ongoingServiceCostAmount: number
  ongoingServiceCostPercentage: number
  productCostAmount: number
  productCostPercentage: number
  initialCostAmount: number
  initialCostPercentage: number
  productTransactionCostAmount: number
  productTransactionCostPercentage: number
  initialDFMCostAmount: number
  initialDFMCostPercentage: number
  ongoingProductDistributorCompensationAmount: number
  ongoingProductDistributorCompensationPercentage: number
  ongoingAdviserCostAmount: number
  ongoingAdviserCostPercentage: number
  ongoingPlatformCostAmount: number
  ongoingPlatformCostPercentage: number
  initialAdviserCostAmount: number
  initialAdviserCostPercentage: number
  initialPlatformCostAmount: number
  initialPlatformCostPercentage: number
  ongoingCostAmount: number
  ongoingCostPercentage: number
}

export interface IFees {
  breakdown: IBreakdownFees
  identifier: string
  name?: string
}

export interface IBreakdownFees {
  recurringTotal: IAnnualAverage
  initial: IAnnualAverageBreakdown
}

export interface IAnnualAverage {
  amount: number
  percentage: number
  breakdown: IAnnualAverageBreakdown[]
}

export interface IAnnualAverageBreakdown {
  amount: number
  name: string,
  percentage: number
  distributorCompensationAmount: number
}

// This is what we get back from Morningstar
export interface IBaseMorningstarData {
  breakdowns: IBreakdowns
  marketValue: number
}

export interface IMorningstarHoldings extends IBaseMorningstarData {
  holdings: IXRayHolding[]
  holdingOverlap: IHoldingOverlap[]
}

// A holding that is present (overlapping) in several of the holdings in a portfolio
export interface IHoldingOverlap {
  holdingId: string
  name: string
  weight: number
  // The sector for the holding. Finance, Tech and so on.
  globalSectorId: string
  parentHoldings: IParentHolding[]
  iSIN: string
}

// This is the data we need in the template to show overlapping holdings
export interface IOverlap {
  // Name of the overlapping holding
  name: string
  iSIN: string
  weight: number
  // The sector. Like Healthcare, Energy, Technology and so one
  sector: string
  children: IOverlapChildren[]
}

// The children of an overlapping holding
export interface IOverlapChildren {
  name: string
  iSIN: string
  weight: number
  weightInSector: number
  date: string
}

// What holding the overlapping holding is present in
export interface IParentHolding {
  holdingId: string
  weight: number
  weightInParent: number
}

/**
 * The breakdown containing asset allocation (what type of holding),
 * regional exposure (what region like Europe) and country exposure (IE what country)
 */
export interface IBreakdowns {
  assetAllocation: IAssetAllocation[]
  regionalExposure?: IRegionalExposure[]
  countryExposure?: ICountryExposure[]
}

// What type of holding
export interface IAssetAllocation {
  type: string
  salePosition: string
  values: IAssetAllocationValuesBase
}

// What region the holding is (like Europe)
export interface IRegionalExposure {
  type: string
  salePosition: string
  values: IRegionalExposureValues
}

// What country the holding is (like Sweden)
export interface ICountryExposure {
  type: string
  salePosition: string
  values: {
    sWE: number
  }
}

// The fees for service and product (I guess holding?) in amount and percent
export interface IFeesOverview {
  serviceAmount: number
  servicePercent: number
  productAmount: number
  productPercent: number
}

// The product fees summary in the report
export interface IProductFeesSummary {
  initialAmount: number
  initialPercent: number
  ongoingAmount: number
  ongoingPercent: number
  distributionAmount: number
  distributionPercent: number
  transactionalAmount: number
  transactionalPercent: number
  extraAmount: number
  extraPercent: number
}

export interface IAssetAllocationValuesBase {
  1: number
  2: number
  3: number
  4: number
  99: number
}

export interface IRegionalExposureValues extends IAssetAllocationValuesBase {
  5: number
  6: number
  7: number
  8: number
  9: number
  10: number
  11: number
  12: number
  13: number
}

// A portfolio containing holdings and other data
export interface IPortfolio {
  // Id of the portfolio
  id: string
  // Name of the portfolio
  name: string
  // What the total wheigt of the portfolio should be in precent
  totalWeight: number
  // If the portfolio is sustainable
  sustainable: boolean
  // The risk level of the portfolio
  risk: string
  // The version of the portfolio
  version: number
  // The holdings in the portfolio
  holdings: IHolding[] | null
  // This is used to describe the risk level of a model portfolio
  info: string[]
}

// The holding that makes up a portfolio
export interface IHolding {
  /**
   * The unique identifier for this holding.
   * When using XHolding it is set to secId or isin depending on
   * type.
   */
  identifier: string
  /**
   * Morningstars own id for the holding
   */
  secId?: string
  /**
   * This is the same as identifier from morningstar
   */
  isin?: string
  /**
   * Type of identifier used for this holding, e.g. ISIN, MSID, etc.
   */
  identifierType: THoldingIdType
  /**
   * Name of this holding
   */
  name: string
  /**
   * The yearly cost
   */
  ongoing: number
  /**
   * The transactional fee
   */
  transaction: number
  /**
   * Distrubution cost
   */
  distributorCompensation: number
  /**
   * Distribution cost in percentage
   */
  distributorCompensationPercentage?: number
  /**
   * The weight of the holding in percent
   */
  weight?: number
  /**
   * Monetary amount of the holding
   */
  amount?: number
}

/**
 * This is a representation of what we get from M*
 * when we request holding data in X-Ray.
 */
export interface IXRayHolding {
  starRatingM255?: number
  returnM12?: number
  returnM36?: number
  returnM60?: number
  // Typically 1,2,3,4 or 99
  holdingType?: keyof IAssetAllocationValuesBase

  // The yearly rate. used to be just ongoing
  ongoingCharge?: number

  // A string like "2020-10-23"
  portfolioDate?: string

  iSIN: string
  securityId?: string

  name: string
  holdingId: number
  weight: number
  securityType: string
}

export interface IMorningstarHolding {
  rows: IMorningstarHoldingRow[]
}

export interface IMorningstarHoldingRow {
  isin: string
  name: string
  secId: string
  distributorCompensationPercentage: number
  ongoingCostActual: number
  transactionFeeActual: number
}

export interface IHorizon {
  purpose: string
  horizon: number | null
  loseImpact: number | null
  withdrawal: number | null
}

/**
 * The map for the different options for the investment horizon in a savings goal
 */
export const HORIZON_PURPOSES = new Map<number, string>([
  [0, 'Mindre än 3 år'],
  [6, '3-4 år'],
  [10, '5-7 år'],
  [18, '8-10 år'],
  [24, 'Minst 10 år']
])

/**
 * The map for the different options for the withdrawal in a savings goal
 */
export const WITHDRAWAL_PURPOSES = new Map<number, string>([
  [0, 'att ta ut mina pengar på en gång'],
  [2, 'att sprida mina uttag under 2-5 år'],
  [3, 'att sprida mina uttag under 6-10 år'],
  [5, 'att sprida mina uttag under 10 år eller mer'],
  [8, 'planerar inte att ta ut mina pengar']
])

export interface IModifiedAccount {
  action: 'sell' | 'buy'
  changes: THoldingsChanges
}


export type TMaritalStatus = 'Single' | 'Married' | 'Divorced' | 'Widowed'
export type TOccupation =
  'Employed'
  | 'Unemployed'
  | 'SelfEmployed'
  | 'Retired'
  | 'Student'
  | 'Other'
export type TYesNo = 'Yes' | 'No'
export type TKnowledgeLevels = 'Low' | 'Medium' | 'High'
export type TInterestLevels = 'None' | 'Low' | 'Medium' | 'High'
export type TFrequency = 'Never' | 'Seldom' | 'Often'
export type TInvestmentAmount =
  'LessThan10'
  | 'Between10-25'
  | 'Between25-50'
  | 'MoreThan50'
export type TFinanicalInstruments =
  'None'
  | 'Multiple'
  | 'HedgeFunds'
  | 'StockAndMixedFunds'
  | 'InterestAndBonds'
export type TImportance = 'NoImportance' | 'Important' | 'VeryImportant'
export type TAvoidMarket =
  'Tobacco'
  | 'Gamboling'
  | 'Pornograf'
  | 'Alcohol'
  | 'Weapons'
  | 'FossilFuels'
  | 'Other'

export interface IProxy {
  name: string
  sub: string
}

export interface IAsset {
  value: number
  loan: number
}

export interface IPrivateAssets {
  house: IAsset
  tenantOwned: IAsset
  vacationHouse: IAsset
  other: IAsset
}

// The basis needed to calculate a risk profile
export interface IRtqBase {
  // Risk tolerance/profile
  /**
   * Question 25 in XLS sheet, yield-expectancy
   */
  riskToleranceYieldExpectancy: number
  /**
   * Question 26 in XLS sheet, acceptable scenarios
   */
  riskToleranceAcceptableScenarios: number
  /**
   * Question 27 in XLS sheet, risk tolerance
   */
  riskToleranceRiskTolerance: number
  /**
   * Question 28 in XLS sheet, investment-loss-actions
   */
  riskToleranceInvestmentLossActions: number
  /**
   * Question 29 in XLS sheet, investment-loss-acceptance
   */
  riskToleranceInvestmentLossAcceptance: number
  /**
   * Question 30 in XLS sheet, drastic-investment-loss-actions
   */
  riskToleranceDrasticInvestmentLossActions: number
  /**
   * Question 31 in XLS sheet, investment-expectation-match
   */
  riskToleranceInvestmentExpectationMatch: number
  // Sustainability
  /**
   * Question 30 in legacy Portfolio/Investment planner, sustainability focus
   */
  sustainabilityFocus: TYesNo
  /**
   * Question 32 in legacy Portfolio/Investment planner,
   *  sustainability finance disclosure regulation importance
   */
  sustainabilityFinanceDisclosureRegulationImportance: TImportance
  /**
   * Question 33 in legacy Portfolio/Investment planner,
   *  sustainability principal adverse impact importance
   */
  sustainabilityPrincipalAdverseImpactAvoidanceImportance: TImportance
  /**
   * Question 31 in legacy Portfolio/Investment planner,
   */
  sustainabilityTaxonomyOperationImportance: TImportance
}

// All the questions in the RTQ
export interface IRiscToleranceQuestionnaire extends IRtqBase {
  rtqId: string
  version: number
  // Family
  maritalStatus: TMaritalStatus
  familyChildren: string
  // Economy
  economyOccupation: TOccupation
  economyIncome: number
  economyExpenditure: number
  economyAssets: IPrivateAssets
  economyLoans: number
  economyFutureImpactFactors: number
  economyAssessedAssets: number

  // Experience
  experienceLength: number
  experienceKnowledge: TKnowledgeLevels
  experienceFinancialInstruments: TFinanicalInstruments
  experienceTradeAmount: number
  experienceLosses: TYesNo
  experienceInterest: TInterestLevels
  experienceSelfMonitoring: TFrequency
  experienceWorkingExperience: TYesNo

  /**
   * Question 34 in legacy Portfolio/Investment planner,
   *  sustainability market risk importance
   */
  sustainabilityOtherMarketRiskImportance: TImportance & 'Specific'

  /**
   * Markets the investor wants to avoid
   */
  sustainabilityAvoidMarkets: TAvoidMarket[]
  /**
   * Question 34 in legacy Portfolio/Investment planner,
   *  sustainability specific risk avoidance
   */
  sustainabilityOtherMarketRiskImportanceNotes: string

  /**
   * The person who is representing the company
   */
  representative: IProxy

  /**
   * If the person has rights to represent the company in this advice
   */
  proxy: TYesNo

  /**
   * A description of the company
   */
  companyDescription: string

  /**
   * The company's last year's profit
   */
  companyProfit: number

  /**
   * The company's assets
   */
  companyAssets: number

  /**
   * The company's liabilities
   */
  companyLiabilities: number

  /**
   * The company's total investable assets
   */
  companyInvestableAssets: number

  /**
   * If the representative have knowledge of anything that
   * can affect the company's future economy
   */
  companyKnowledgeEconomicFuture: string

  /**
   * How many percent the company is planning to invest
   */
  companyInvestmentAmount: TInvestmentAmount
}

export interface INotes {
  // The notes that are just visible to the adviser.
  adviserNote: string
  // The notes that go in the summary for the client
  adviceNote: string
}

/**
 * Interface that we use in the components that make up the RTQ when we send
 * data to the parent component.
 */
export interface IValidData {
  // If the form control is valid
  valid: boolean
  // If the form control i pristine
  pristine: boolean
  // The name of the form control
  prop: string
}

/**
 * This is only used to generat data for static risk profiles that are shown
 * in the top of every savings goal
 */
export const RISK_PROFILES: IPortfolio[] = [
  {
    id: '1',
    name: '(A) Lägst Risk',
    risk: 'A',
    info: [
      'En mycket försiktig investerare är väldigt orolig för förluster eller har endast en mycket kort placeringshorisont.',
      'En mycket försiktig investerare är alltid villig att acceptera en mycket lägre avkastning i syfte att undvika förluster.',
      'En mycket försiktig investerare är likväl villig att acceptera en viss marknadsrisk i syfte att försöka uppnå bättre avkastning än vad som kan fås genom sparande på bankkonto.'
    ],
    holdings: null,
    version: 1,
    totalWeight: 100,
    sustainable: false
  },
  {
    id: '2',
    name: '(A) Lägst Risk',
    risk: 'A',
    info: [
      'En mycket försiktig investerare är väldigt orolig för förluster eller har endast en mycket kort placeringshorisont.',
      'En mycket försiktig investerare är alltid villig att acceptera en mycket lägre avkastning i syfte att undvika förluster.',
      'En mycket försiktig investerare är likväl villig att acceptera en viss marknadsrisk i syfte att försöka uppnå bättre avkastning än vad som kan fås genom sparande på bankkonto.'
    ],
    holdings: null,
    version: 1,
    totalWeight: 100,
    sustainable: true
  },
  {
    id: '3',
    name: '(B) Mycket Låg risk - Hållbar',
    risk: 'B',
    info: [
      'En försiktig investerare är orolig för förluster eller har endast en kort eller mycket kort placeringshoristont.',
      'En mycket försiktig investerare är villig att acceptera en lägre avkastning i syfte att undvika förluster.',
      'En mycket försiktig investerare är likväl villig att acceptera en viss marknadsrisk i syfte att försöka uppnå bättre avkastning än vad som kan fås genom sparande på bankkonto.'
    ],
    holdings: null,
    version: 1,
    totalWeight: 100,
    sustainable: true
  },
  {
    id: '4',
    name: '(B) Mycket Låg risk',
    risk: 'B',
    info: [
      'En försiktig investerare är orolig för förluster eller har endast en kort eller mycket kort placeringshoristont.',
      'En mycket försiktig investerare är villig att acceptera en lägre avkastning i syfte att undvika förluster.',
      'En mycket försiktig investerare är likväl villig att acceptera en viss marknadsrisk i syfte att försöka uppnå bättre avkastning än vad som kan fås genom sparande på bankkonto.'
    ],
    holdings: null,
    version: 1,
    totalWeight: 100,
    sustainable: false
  },
  {
    id: '5',
    name: '(C) Låg risk - Hållbar',
    risk: 'C',
    info: [
      'En måttligt försiktig investerare hyser viss oro inför potentiella förluster och kan både ha en kortare eller längre placeringshorisont.',
      'En måttligt försiktig investerare känner sig obekväm med att ta större risker med sin investering, men är likväl villig att ta viss risk i syfte att uppnå större möjligheter till högre avkastning.'
    ],
    holdings: null,
    version: 1,
    totalWeight: 100,
    sustainable: true
  },
  {
    id: '6',
    name: '(C) Låg risk',
    risk: 'C',
    info: [
      'En måttligt försiktig investerare hyser viss oro inför potentiella förluster och kan både ha en kortare eller längre placeringshorisont.',
      'En måttligt försiktig investerare känner sig obekväm med att ta större risker med sin investering, men är likväl villig att ta viss risk i syfte att uppnå större möjligheter till högre avkastning.'
    ],
    holdings: null,
    version: 1,
    totalWeight: 100,
    sustainable: false
  },
  {
    id: '7',
    name: '(D) Medel risk - Hållbar',
    risk: 'D',
    info: [
      'Du föredrar att ha balans i ditt sparande. Du kan acceptera mindre svängningar i värdeutvecklingen men förväntar dig då också en något högre avkastning på sikt.',
      'En måttlig investerare oroas inte så mycket av risk och accepterar kortsiktiga marknadsrörelser i syfte att uppnå möjligheten till bättre avkastning.',
      'En måttlig investerare söker i sitt investeringsval en medelväg mellan avkastning och risktagande.',
      'En måttlig investerare behöver inte ta ut sina pengar vid en marknadsnedgång utan kan behålla sin investering i syfte att återhämta sina förluster.'
    ],
    holdings: null,
    version: 1,
    totalWeight: 100,
    sustainable: true
  },
  {
    id: '8',
    name: '(D) Medel risk',
    risk: 'D',
    info: [
      'Du föredrar att ha balans i ditt sparande. Du kan acceptera mindre svängningar i värdeutvecklingen men förväntar dig då också en något högre avkastning på sikt.',
      'En måttlig investerare oroas inte så mycket av risk och accepterar kortsiktiga marknadsrörelser i syfte att uppnå möjligheten till bättre avkastning.',
      'En måttlig investerare söker i sitt investeringsval en medelväg mellan avkastning och risktagande.',
      'En måttlig investerare behöver inte ta ut sina pengar vid en marknadsnedgång utan kan behålla sin investering i syfte att återhämta sina förluster.'
    ],
    holdings: null,
    version: 1,
    totalWeight: 100,
    sustainable: false
  },
  {
    id: '9',
    name: '(E) Hög risk - Hållbar',
    risk: 'E',
    info: [
      'En måttligt äventyrlig investerares huvudsyfte är att öka värdet på sin investering och man är villig att acceptera risken att förlora delar av sitt investerade kapital för att uppnå detta.',
      'En måttligt äventyrlig investerare värdesätter möjligheten till avkastning över tryggheten med lägre risk.',
      'En måttligt äventyrlig investerare är villig att behålla sin investering trots en betydande värdeminskning i syfte att återställa sina förluster och för att uppnå sina långsiktiga investeringsmål.'
    ],
    holdings: null,
    version: 1,
    totalWeight: 100,
    sustainable: true
  },
  {
    id: '10',
    name: '(E) Hög risk',
    risk: 'E',
    info: [
      'En måttligt äventyrlig investerares huvudsyfte är att öka värdet på sin investering och man är villig att acceptera risken att förlora delar av sitt investerade kapital för att uppnå detta.',
      'En måttligt äventyrlig investerare värdesätter möjligheten till avkastning över tryggheten med lägre risk.',
      'En måttligt äventyrlig investerare är villig att behålla sin investering trots en betydande värdeminskning i syfte att återställa sina förluster och för att uppnå sina långsiktiga investeringsmål.'
    ],
    holdings: null,
    version: 1,
    totalWeight: 100,
    sustainable: false
  },
  {
    id: '11',
    name: '(F) Mycket Hög Risk - Hållbar',
    risk: 'F',
    info: [
      'En äventyrlig investerares överordnade mål är att öka värdet på sin investering och man är villig att acceptera risken att förlora delar av sitt investerade kapital för att uppnå detta.',
      'En äventyrlig investerare värdesätter alltid möjligheten till avkastning över tryggheten med lägre risk.',
      'En äventyrlig investerare är villig att behålla sin investering trots en mycket stor värdeminskning i syfte att återställa sina förluster och för att uppnå sina långsiktiga investeringsmål.'
    ],
    holdings: null,
    version: 1,
    totalWeight: 100,
    sustainable: true
  },
  {
    id: '12',
    name: '(F) Mycket Hög Risk',
    risk: 'F',
    info: [
      'En äventyrlig investerares överordnade mål är att öka värdet på sin investering och man är villig att acceptera risken att förlora delar av sitt investerade kapital för att uppnå detta.',
      'En äventyrlig investerare värdesätter alltid möjligheten till avkastning över tryggheten med lägre risk.',
      'En äventyrlig investerare är villig att behålla sin investering trots en mycket stor värdeminskning i syfte att återställa sina förluster och för att uppnå sina långsiktiga investeringsmål.'
    ],
    holdings: null,
    version: 1,
    totalWeight: 100,
    sustainable: false
  },
  {
    id: '13',
    name: '(G) Högst Risk - Hållbar',
    risk: 'G',
    info: [
      'En mycket äventyrlig investerare strävar alltid efter att uppnå högsta möjliga avkastning på lång sikt och oroas mycket lite för eventuella kortsiktiga marknadsnedgångar.',
      'En mycket äventyrlig investerare värdesätter alltid möjligheten till hög avkastning över tryggheten med lägre risk.',
      'En mycket äventyrlig investerare är mest fokuserad på hög avkastning och kan acceptera både stora och frekventa nedgångar i värdet av sin investering över tid i utbyte mot möjligheten till högre avkastning på lång sikt.'
    ],
    holdings: null,
    version: 1,
    totalWeight: 100,
    sustainable: true
  },
  {
    id: '14',
    name: '(G) Högst Risk',
    risk: 'G',
    info: [
      'En mycket äventyrlig investerare strävar alltid efter att uppnå högsta möjliga avkastning på lång sikt och oroas mycket lite för eventuella kortsiktiga marknadsnedgångar.',
      'En mycket äventyrlig investerare värdesätter alltid möjligheten till hög avkastning över tryggheten med lägre risk.',
      'En mycket äventyrlig investerare är mest fokuserad på hög avkastning och kan acceptera både stora och frekventa nedgångar i värdet av sin investering över tid i utbyte mot möjligheten till högre avkastning på lång sikt.'
    ],
    holdings: null,
    version: 1,
    totalWeight: 100,
    sustainable: false
  },
  {
    id: '15',
    name: '(H) Ingen investering - Hållbar',
    risk: 'H',
    info: [
      'Investerare som faller i denna kategori kommer inte att finna någon passande investeringsportfölj då deras viktigaste mål är att hålla sina pengar helt säkra, även under korta perioder. Investerare i denna kategori bör ha en ytterligare diskussion med sin rådgivare för att se till att de förstår varför riskanalysfrågeformuläret har resulterat i detta resultat'
    ],
    holdings: null,
    version: 1,
    totalWeight: 100,
    sustainable: true
  },
  {
    id: '16',
    name: '(H) Ingen investering',
    risk: 'H',
    info: [
      'Investerare som faller i denna kategori kommer inte att finna någon passande investeringsportfölj då deras viktigaste mål är att hålla sina pengar helt säkra, även under korta perioder. Investerare i denna kategori bör ha en ytterligare diskussion med sin rådgivare för att se till att de förstår varför riskanalysfrågeformuläret har resulterat i detta resultat'
    ],
    holdings: null,
    version: 1,
    totalWeight: 100,
    sustainable: false
  }
]

export const RISK_PROFILES_DATA = new Map<string, IMorningstarHoldings>([
  [
    '1',
    {
      holdings: [],
      holdingOverlap: [],
      breakdowns: {
        assetAllocation: [{
          salePosition: 'N',
          type: '"MorningstarEUR3"',
          values: {'1': 0, '2': 100, '3': 0, '4': 0, '99': 0}
        }]
      },
      marketValue: 0
    }
  ],
  [
    '2',
    {
      holdings: [],
      holdingOverlap: [],
      breakdowns: {
        assetAllocation: [{
          salePosition: 'N',
          type: '"MorningstarEUR3"',
          values: {'1': 0, '2': 100, '3': 0, '4': 0, '99': 0}
        }]
      },
      marketValue: 0
    }
  ],
  [
    '3',
    {
      holdings: [],
      holdingOverlap: [],
      breakdowns: {
        assetAllocation: [{
          salePosition: 'N',
          type: '"MorningstarEUR3"',
          values: {'1': 0, '2': 100, '3': 0, '4': 0, '99': 0}
        }]
      },
      marketValue: 0
    }
  ],
  [
    '4',
    {
      holdings: [],
      holdingOverlap: [],
      breakdowns: {
        assetAllocation: [{
          salePosition: 'N',
          type: '"MorningstarEUR3"',
          values: {'1': 0, '2': 100, '3': 0, '4': 0, '99': 0}
        }]
      },
      marketValue: 0
    }
  ],
  [
    '5',
    {
      holdings: [],
      holdingOverlap: [],
      breakdowns: {
        assetAllocation: [{
          salePosition: 'N',
          type: '"MorningstarEUR3"',
          values: {'1': 20, '2': 80, '3': 0, '4': 0, '99': 0}
        }]
      },
      marketValue: 0
    }
  ],
  [
    '6',
    {
      holdings: [],
      holdingOverlap: [],
      breakdowns: {
        assetAllocation: [{
          salePosition: 'N',
          type: '"MorningstarEUR3"',
          values: {'1': 20, '2': 80, '3': 0, '4': 0, '99': 0}
        }]
      },
      marketValue: 0
    }
  ],
  [
    '7',
    {
      holdings: [],
      holdingOverlap: [],
      breakdowns: {
        assetAllocation: [{
          salePosition: 'N',
          type: '"MorningstarEUR3"',
          values: {'1': 50, '2': 50, '3': 0, '4': 0, '99': 0}
        }]
      },
      marketValue: 0
    }
  ],
  [
    '8',
    {
      holdings: [],
      holdingOverlap: [],
      breakdowns: {
        assetAllocation: [{
          salePosition: 'N',
          type: '"MorningstarEUR3"',
          values: {'1': 50, '2': 50, '3': 0, '4': 0, '99': 0}
        }]
      },
      marketValue: 0
    }
  ],
  [
    '9',
    {
      holdings: [],
      holdingOverlap: [],
      breakdowns: {
        assetAllocation: [{
          salePosition: 'N',
          type: '"MorningstarEUR3"',
          values: {'1': 80, '2': 20, '3': 0, '4': 0, '99': 0}
        }]
      },
      marketValue: 0
    }
  ],
  [
    '10',
    {
      holdings: [],
      holdingOverlap: [],
      breakdowns: {
        assetAllocation: [{
          salePosition: 'N',
          type: '"MorningstarEUR3"',
          values: {'1': 80, '2': 20, '3': 0, '4': 0, '99': 0}
        }]
      },
      marketValue: 0
    }
  ],
  [
    '11',
    {
      holdings: [],
      holdingOverlap: [],
      breakdowns: {
        assetAllocation: [{
          salePosition: 'N',
          type: '"MorningstarEUR3"',
          values: {'1': 90, '2': 10, '3': 0, '4': 0, '99': 0}
        }]
      },
      marketValue: 0
    }
  ],
  [
    '12',
    {
      holdings: [],
      holdingOverlap: [],
      breakdowns: {
        assetAllocation: [{
          salePosition: 'N',
          type: '"MorningstarEUR3"',
          values: {'1': 90, '2': 10, '3': 0, '4': 0, '99': 0}
        }]
      },
      marketValue: 0
    }
  ],
  [
    '13',
    {
      holdings: [],
      holdingOverlap: [],
      breakdowns: {
        assetAllocation: [{
          salePosition: 'N',
          type: '"MorningstarEUR3"',
          values: {'1': 100, '2': 0, '3': 0, '4': 0, '99': 0}
        }]
      },
      marketValue: 0
    }
  ],
  [
    '14',
    {
      holdings: [],
      holdingOverlap: [],
      breakdowns: {
        assetAllocation: [{
          salePosition: 'N',
          type: '"MorningstarEUR3"',
          values: {'1': 100, '2': 0, '3': 0, '4': 0, '99': 0}
        }]
      },
      marketValue: 0
    }
  ],
  [
    '15',
    {
      holdings: [],
      holdingOverlap: [],
      breakdowns: {
        assetAllocation: [{
          salePosition: 'N',
          type: '"MorningstarEUR3"',
          values: {'1': 0, '2': 0, '3': 0, '4': 0, '99': 0}
        }]
      },
      marketValue: 0
    }
  ],
  [
    '16',
    {
      holdings: [],
      holdingOverlap: [],
      breakdowns: {
        assetAllocation: [{
          salePosition: 'N',
          type: '"MorningstarEUR3"',
          values: {'1': 0, '2': 0, '3': 0, '4': 0, '99': 0}
        }]
      },
      marketValue: 0
    }
  ]
])

/**
 * The ids of the graph images for the PDF report
 */
export type TImageId = 'regionalExposureOriginal' | 'regionalExposureNew'

/**
 * This is the object we tranfer to BE it has an "id"
 * so that we can find it in backend and the actual
 * image which is an image uro data:image/png:ASDE123aaaE...
 */
export interface IImage {
  // An id that you select per image
  id: TImageId
  // What savings goal it belongs in
  savingsGoalId: string
  // URI-encoded image
  image: string
}

export const RISK_HORIZON_MATRIX = [
  ['H', 'A', 'B', 'C'],
  ['H', 'B', 'C', 'D'],
  ['H', 'C', 'D', 'E'],
  ['H', 'D', 'E', 'F'],
  ['H', 'E', 'F', 'G']
]

/**
 * The data PDF report.
 * This is just stupid and can be removed. Just use the typing that already
 * exists. This will be removed here and rewritten together with the BE
 */
export interface IDocumentData {
  horizon: IHorizon
  savingsGoalId: string
  usedAccounts: IUsedAccount[]
  riskProfile: IDocumentRiskProfile
  suggestedPortfolioOverview: IDocumentSuggestedPortfolioOverview
  costOverviewNew: IDocumentCostOverview
  costOverviewOriginal: IDocumentCostOverview
  portfolioOverviewNew: IDocumentPortfolioOverview
  portfolioOverviewOriginal: IDocumentPortfolioOverview
  ongoingProductFeesNew: IDocumentOngoingProductFees
  ongoingProductFeesOriginal: IDocumentOngoingProductFees
  regionalExposureOriginal: IDocumentRegionalExposure
  regionalExposureNew: IDocumentRegionalExposure
  overlap: IDocumentOverlap
  portfolioComparisonHoldingsCosts: IDocumentPortfolioComparisonHoldingsCosts
  costAssumption: IDocumentCostAssumption
  costInitialOriginal: IDocumentCostInitial
  costInitialNew: IDocumentCostInitial
  costOngoingOriginal: IDocumentCostOngoing
  costOngoingNew: IDocumentCostOngoing
  holdingFees: IDocumentHoldingFees
}

/**
 * Risk profile for the PDF report
 */
export interface IDocumentRiskProfile {
  data: IDocumentRiskProfileData
}

/**
 * Suggested portfolio for the PDF report
 */
export interface IDocumentSuggestedPortfolioOverview {
  data: ITableData[]
}

/**
 * Cost overview for the PDF report
 */
export interface IDocumentCostOverview {
  data: IProductServiceCost
}

/**
 * Ongoing fees for products for the PDF report
 */
export interface IDocumentOngoingProductFees {
  data: IFeesProductBase
}

/**
 * Overlaps for the PDF report
 */
export interface IDocumentOverlap {
  data: IOverlaps
}

/**
 * Comparison of the portfolios holding costs for the PDF report
 */
export interface IDocumentPortfolioComparisonHoldingsCosts {
  data: IPortfolioComparisonHoldingsCosts
}

/**
 * Cost assumptions for the PDF report
 */
export interface IDocumentCostAssumption {
  data: ICostAssumptions
}

/**
 * Initial costs for the PDF report
 */
export interface IDocumentCostInitial {
  data: IProductServiceCost
}

/**
 * Ongoing costs for the PDF report
 */
export interface IDocumentCostOngoing {
  data: IProductServiceCost
}

/**
 * Initial service costs for the PDF report
 */
export interface IDocumentCostServiceInitial {
  data: IProductServiceCost
}

/**
 * Ongoing service costs for the PDF data
 */
export interface IDocumentCostServiceOngoing {
  data: IProductServiceCost
}

/**
 * Holding fees for the PDF report
 */
export interface IDocumentHoldingFees {
  data: IFeesHoldings
}

/**
 * Regional exposure for the PDF report
 */
export interface IDocumentRegionalExposure {
  data: IRegionalCountryExposure
  image: string
}

export interface IDocumentPortfolioOverviewData {
  breakdownData: ITableData[]
  tableData: TableHolding[]
}
export interface IDocumentPortfolioOverview {
  data: IDocumentPortfolioOverviewData
}


/**
 * The data for the risk profile in the PDF report
 */
export interface IDocumentRiskProfileData {
  breakdownData: BreakdownData
  name: string
  text: string[]
}

/**
 * This is how a customer looks at the BE
 */
export interface ICustomerReport {
  name: string
  personalNumber: string
  email: string
  mobilePhone: string
}

/**
 * The data that is sent to the BE for the creation of the PDF report
 */
export interface IReport {
  customer: ICustomerReport
  fundFactSheetIds: string[]
  includeAllFundFactSheets: boolean
  advisor: IAdvisor
  advisorNotes: string
  savingsGoals: IDocumentData[]
  rtqAnswers: IRiscToleranceQuestionnaire
}

/**
 * Original and new cost assumptions in the report
 */
export interface ICostAssumptions {
  original: ICostAssumption
  new: ICostAssumption

}

/**
 * The cost assumption values
 */
export interface ICostAssumption {
  returnIfNoFees: number
  returnAfterFees: number
  reductionInReturn: number
  or: number
  summary: number
}

/**
 * Original and new regional and country exposure in the report
 */
export interface IRegionalCountryExposures {
  original: IRegionalCountryExposure
  new: IRegionalCountryExposure
}

/**
 * The regional and country exposure values
 */
export interface IRegionalCountryExposure {
  swe: number
  regional: IRegionalExposureValues
}

/**
 * Original and new overlapping holdings in the report
 */
export interface IOverlaps {
  original: IOverlap[]
  new: IOverlap[]
}

/**
 * Original and new portfolio holding cost comparison in the report
 */
export interface IPortfolioComparisonHoldingsCosts {
  original: IPortfolioComparisonHoldingsCost[]
  new: IPortfolioComparisonHoldingsCost[]
}

/**
 * The portfolio holding cost comparison values
 */
export interface IPortfolioComparisonHoldingsCost {
  name: string
  date: string
  weight: number
  fee: number
}

/**
 * Original and new costs for product services in the report
 */
export interface IProductServiceCosts {
  original: IProductServiceCost
  new: IProductServiceCost
}

/**
 * Product services cost values
 */
export interface IProductServiceCost {
  serviceAmount: number
  servicePercent: number
  productAmount: number
  productPercent: number
}

/**
 * Original and new fees for products in the report
 */
export interface IFeesProducts {
  original: IFeesProductExtras
  new: IFeesProductExtras
}

/**
 * Product fees values
 */
export interface IFeesProductBase {
  initialAmount: number
  initialPercent: number
  ongoingAmount: number
  ongoingPercent: number
  transactionalAmount: number
  transactionalPercent: number
  distributionAmount: number
  distributionPercent: number
}

/**
 * Sometimes IFeesProduct have extras
 */
export interface IFeesProductExtras extends IFeesProductBase{
  extraAmount: number
  extraPercent: number
}

/**
 * Fees for holdings needs a name in the report
 */
export interface IFeesHolding extends IFeesProductBase {
  name: string
}

/**
 * Original and new fees for holdings in the report
 */
export interface IFeesHoldings {
  original: IFeesHolding[]
  new: IFeesHolding[]
}

/**
 * The region we use in the Google Map
 */
export interface IRegion {
  region: TMSRegion
}

/**
 * A Morningstar regional mapping
 */
export interface TMSRegion {

  /**
   * The Morningstar name of the region
   */
  name: string,

  /**
   * The Morningstar SE name of the region
   */
  seName: string,

  /**
   * The countries covered by the region, using ISO 3166-1 alpha-2 codes or
   * country names in english
   */
  countries: string[]
}

export type TMSRegionalMapping = Record<number, TMSRegion>

export const msRegionalMapping: Readonly<TMSRegionalMapping> = {
  0: {
    name: 'Sverige',
    seName: 'Sverige',
    countries: ['Sweden']
  },
  1: {
    name: 'United States',
    seName: 'Nordamerika',
    countries: ['USA', 'Canada', 'Mexico']
  },
  2: {
    name: 'Canada',
    seName: 'Kanada',
    countries: ['Canada']
  },
  3: {
    name: 'Latin America',
    seName: 'Latinamerika',
    countries: [
      'Argentina', 'Bolivia', 'Brazil', 'Chile', 'Colombia', 'Costa Rica', 'Cuba', 'Dominican Republic', 'Ecuador', 'El Salvador', 'Guatemala', 'Haiti', 'Honduras', 'Mexico', 'Nicaragua', 'Panama', 'Paraguay', 'Peru', 'Puerto Rico', 'Uruguay', 'Venezuela'
    ]
  },
  4: {
    name: 'United Kingdom',
    seName: 'Storbritannien',
    countries: ['United Kingdom']
  },
  5: {
    name: 'Eurozone',
    seName: 'Euroland',
    countries: [
      'Austria', 'Belgium', 'Bulgaria', 'Croatia', 'Cyprus', 'Czech Republic', 'Denmark', 'Estonia', 'Finland', 'France', 'Germany', 'Greece', 'Hungary', 'Ireland', 'Italy', 'Latvia', 'Lithuania', 'Luxembourg', 'Malta', 'Netherlands', 'Poland', 'Portugal', 'Romania', 'Slovakia', 'Slovenia', 'Spain', 'Sweden',
      'French Guina'
    ]
  },
  6: {
    name: 'Europe - ex Euro',
    seName: 'Europa exkl euroland',
    countries: [
      'Albania', 'Andorra', 'Armenia', 'Azerbaijan', 'Belarus', 'Bosnia and Herzegovina', 'Georgia', 'Iceland', 'Kosovo', 'Liechtenstein', 'Moldova', 'Monaco', 'Montenegro', 'North Macedonia', 'Norway', 'San Marino', 'Serbia', 'Sweden', 'Switzerland', 'Turkey', 'Ukraine', 'United Kingdom', 'Vatican City'
    ]
  },
  7: {
    name: 'Europa - Emerging',
    seName: 'Europa, tillväxtmarknader',
    countries: [
      // Central and Eastern Europe (CEE)
      'Poland', 'Hungary', 'Czech Republic', 'Slovakia', 'Romania', 'Bulgaria', 'Croatia', 'Slovenia',
      // Balkans
      'Serbia', 'Bosnia and Herzegovina', 'North Macedonia', 'Albania', 'Montenegro',
      // Eastern Europe (EEU)
      'Ukraine', 'Moldova', 'Georgia', 'Armenia',
      // Them bastards
      'Russia'
    ]
  },
  8: {
    name: 'Africa',
    seName: 'Afrika',
    countries: [
      'Algeria', 'Angola', 'Benin', 'Botswana', 'Burkina Faso', 'Burundi', 'Cabo Verde', 'Cameroon', 'Central African Republic', 'Chad', 'Comoros', 'Democratic Republic of the Congo', 'Republic of the Congo', 'Ivory Coast', 'Djibouti', 'Egypt', 'Equatorial Guinea', 'Eritrea', 'Eswatini', 'Ethiopia', 'Gabon', 'Gambia', 'Ghana', 'Guinea', 'Guinea-Bissau', 'Kenya', 'Lesotho', 'Liberia', 'Libya', 'Madagascar', 'Malawi', 'Mali', 'Mauritania', 'Mauritius', 'Morocco', 'Mozambique', 'Namibia', 'Niger', 'Nigeria', 'Rwanda', 'São Tomé and Príncipe', 'Senegal', 'Seychelles', 'Sierra Leone', 'Somalia', 'South Africa', 'South Sudan', 'Sudan', 'Tanzania', 'Togo', 'Tunisia', 'Uganda', 'Zambia', 'Zimbabwe', 'Western Sahara'
    ]
  },
  9: {
    name: 'Middle East',
    seName: 'Mellanöstern',
    countries: [
      'Bahrain', 'Cyprus', 'Iran', 'Iraq', 'Israel', 'Jordan', 'Kuwait', 'Lebanon', 'Oman', 'Palestine', 'Qatar', 'Saudi Arabia', 'Syria', 'Turkey', 'United Arab Emirates', 'Yemen'
    ]
  },
  10: {
    name: 'Japan',
    seName: 'Japan',
    countries: ['Japan']
  },
  11: {
    name: 'Australasia',
    seName: 'Australasien',
    countries: ['Australia', 'New Zealand', 'Papua New Guinea', 'Fiji', 'Solomon Islands', 'Vanuatu', 'New Caledonia', 'East Timor', 'Indonesia']
  },
  12: {
    name: 'Asia - Developed',
    seName: 'Asien',
    countries: ['China', 'India', 'Taiwan']
  },
  13: {
    name: 'Asia - Emerging',
    seName: 'Asien, tillväxtmarknader',
    countries: ['North Korea']
  },
  14: {
    name: 'Emerging Market',
    seName: 'Tillväxtmarknader',
    countries: []
  },
  15: {
    name: 'Developed Country',
    seName: 'Utvecklade länder',
    countries: []
  },
  99: {
    name: 'Not Classified',
    seName: 'Ej klassificerat',
    countries: []
  }
}

/**
 * What we need to send to Morningstar for costs
 */
export interface IMorningstarHoldingCosts {
  id: string
  holdings: IHolding[]
}

/**
 * A document (PDF) that we get from the BE
 */
export interface IDocument {
  // The documents id
  documentId: string
  // The advice id of the advice the document belongs to
  adviceId: string
  // The date it was signed
  signedAt: string
  // What method was used to sign. IE Signeringsrummet or with a physical penn
  signingMethod: TSignStatus
}

// What we receive from BE when asking for a signed URL
export interface ISignedUrl {
  // The document id
  id: string
  // The signed URL
  url: string
}